import React from "react"

import {
  yesNoBadgeColorClasses,
  yesNoBadgeShapeClasses,
} from "@/metrics/metric-display"
import { useMetricRecordMetadataById } from "@/metrics/transport-data"
import { Badge } from "@coval/ui/badge"
import { Card, CardContent } from "@coval/ui/card"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import {
  DerivedStrictLogicConfig,
  type StrictLogicMetadata,
} from "../../components/derived/DerivedStrictLogicConfig"
import { CategoricalMetricRecord } from "../base/CategoricalMetricRecord"

interface ConditionEvaluated {
  parent_metric_id: string
  operator: string
  threshold: number | string
  observed: number | string | null
  passed: boolean
}

interface DerivedStrictLogicResult {
  matched: boolean
  join_operator: "AND" | "OR"
  conditions_evaluated: ConditionEvaluated[]
}

const JOIN_OPERATOR_LABEL: Record<string, string> = {
  AND: "ALL conditions met",
  OR: "ANY condition met",
}

const VERDICT_DISPLAY: Record<string, string> = {
  MET: "Met",
  NOT_MET: "Not Met",
}

function parseDerivedStrictLogicResult(
  mo: RunMetricOutput,
): DerivedStrictLogicResult | null {
  const result = mo.result as Record<string, unknown> | undefined
  if (!result) {
    return null
  }
  if (
    typeof result.matched !== "boolean" ||
    !Array.isArray(result.conditions_evaluated)
  ) {
    return null
  }
  return result as unknown as DerivedStrictLogicResult
}

const ConditionsTable: React.FC<{
  conditions: ConditionEvaluated[]
}> = ({ conditions }) => {
  const { metricRecordsById } = useMetricRecordMetadataById()

  return (
    <div className="overflow-x-auto">
      <table
        className="w-full text-sm table-fixed"
        aria-label="Condition breakdown"
      >
        <colgroup>
          <col className="w-2/5" />
          <col className="w-16" />
          <col className="w-1/6" />
          <col className="w-1/6" />
          <col className="w-12" />
        </colgroup>
        <thead>
          <tr className="text-left text-coval-text-muted border-b border-coval-border">
            <th className="pb-2 pr-4 font-medium">Parent metric</th>
            <th className="pb-2 pr-4 font-medium">Operator</th>
            <th className="pb-2 pr-4 font-medium">Threshold</th>
            <th className="pb-2 pr-4 font-medium">Observed</th>
            <th className="pb-2 font-medium">Pass</th>
          </tr>
        </thead>
        <tbody>
          {conditions.map((cond, index) => {
            const name =
              metricRecordsById[cond.parent_metric_id]?.metric_display_name ??
              cond.parent_metric_id
            return (
              <tr
                key={`${cond.parent_metric_id}-${index}`}
                className="border-b border-coval-border last:border-0"
              >
                <td className="py-2 pr-4 text-coval-text truncate">{name}</td>
                <td className="py-2 pr-4">{cond.operator}</td>
                <td className="py-2 pr-4 break-words">
                  {String(cond.threshold)}
                </td>
                <td className="py-2 pr-4 break-words">
                  {cond.observed == null ? (
                    <span
                      className="text-coval-text-muted italic"
                      title="Parent value was not available — metric SKIPPED"
                      aria-label="SKIPPED"
                    >
                      SKIPPED
                    </span>
                  ) : (
                    String(cond.observed)
                  )}
                </td>
                <td className="py-2">
                  <span
                    className={
                      cond.passed
                        ? "text-coval-success-text font-medium"
                        : "text-coval-danger-text font-medium"
                    }
                    aria-label={cond.passed ? "passed" : "failed"}
                  >
                    {cond.passed ? "✓" : "✗"}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export class DerivedStrictLogicMetricRecord extends CategoricalMetricRecord {
  compareValues(
    value1: { string_value?: string | null },
    value2: { string_value?: string | null },
  ): boolean {
    return value1.string_value === value2.string_value
  }

  // Run-table cell: MET → success chip, NOT_MET → danger chip, anything else → default.
  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    const verdict = mo.string_value
    if (verdict === "MET") {
      return (
        <span
          className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses.success}`}
        >
          {VERDICT_DISPLAY.MET}
        </span>
      )
    }
    if (verdict === "NOT_MET") {
      return (
        <span
          className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses.danger}`}
        >
          {VERDICT_DISPLAY.NOT_MET}
        </span>
      )
    }
    return (
      <span
        className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses.default}`}
      >
        N/A
      </span>
    )
  }

  // Single-output card value (e.g. metric viewer summary): colored verdict text
  // matching the composite metric's Pass/Fail card-value treatment.
  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode | null {
    const verdict = mo.string_value
    if (verdict === "MET") {
      return (
        <div className="text-2xl font-semibold text-coval-success-text">
          {VERDICT_DISPLAY.MET}
        </div>
      )
    }
    if (verdict === "NOT_MET") {
      return (
        <div className="text-2xl font-semibold text-coval-danger-text">
          {VERDICT_DISPLAY.NOT_MET}
        </div>
      )
    }
    if (verdict == null) {
      return null
    }
    return (
      <div className="text-2xl font-semibold text-coval-text">{verdict}</div>
    )
  }

  _renderDeepDive(mo: RunMetricOutput, ..._rest: unknown[]): React.ReactNode {
    const parsed = parseDerivedStrictLogicResult(mo)
    const verdict = mo.string_value ?? "—"
    const isMet = verdict === "MET"
    const verdictLabel = VERDICT_DISPLAY[verdict] ?? verdict

    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col items-center gap-2">
              <Badge
                variant={isMet ? "success" : "danger"}
                size="chip"
                aria-label={`Verdict: ${verdictLabel}`}
                className="text-base font-bold tracking-wide"
              >
                {verdictLabel}
              </Badge>
              {parsed && (
                <span className="text-xs text-coval-text-muted">
                  {JOIN_OPERATOR_LABEL[parsed.join_operator] ??
                    parsed.join_operator}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {parsed && parsed.conditions_evaluated.length > 0 && (
          <Card>
            <CardContent className="pt-4">
              <ConditionsTable conditions={parsed.conditions_evaluated} />
            </CardContent>
          </Card>
        )}
      </div>
    )
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
  ): React.ReactNode {
    if (!metricRecordData.metric_metadata) {
      return null
    }
    const metadata = metricRecordData.metric_metadata as StrictLogicMetadata
    const handleChange = (next: StrictLogicMetadata) => {
      onChange({ ...metricRecordData, metric_metadata: next })
    }
    return (
      <DerivedStrictLogicConfig
        metadata={metadata}
        onChange={handleChange}
        currentMetricId={metricRecordData.id ?? null}
      />
    )
  }

  renderMetricType(): React.ReactNode {
    return "Derived Strict Logic"
  }
}
